import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ExportToCsv } from 'export-to-csv';
import { switchMap } from 'rxjs/operators';

import { AppComponent } from '../app.component';
import { HubService } from '../services/hub.service';
import { CustomerType } from '../types/customer-type';
import { Hub } from '../types/hub';
import { HubInformation } from '../types/hub-information';
import { InstallationState } from '../types/installation-state';
import { Installer } from '../types/installer';
import { Shipment } from '../types/shipment';
import { StockLocation } from '../types/stock-location';
import { StockOfficeStatus } from '../types/stock-office-status';
import { StockStatus } from '../types/stock-status';

const seaiAll = 999;

@Component({
  selector: 'stock-list',
  templateUrl: './stock.component.html',
  styleUrls: ['./stock.component.css'],
  providers: [HubService],
})

export class HubStockComponent implements OnInit {
  hubinfo = <HubInformation[]>[];
  errorMessage: string;
  public modifyPanelVisible = false;
  customer_types = <CustomerType[]>[];
  stock_statuses = <StockStatus[]>[];
  stock_locations = <StockLocation[]>[];
  installation_states = <InstallationState[]>[];
  installer_list = [] as Installer[];
  shipment_list = [] as Shipment[];
  stock_office_status = <StockOfficeStatus[]>[];

  public getSerial = Hub.HubIDToSerial;
  public ascDesc: string[] = ['ASC', 'DESC'];
  lastSortOrder = '';
  cartons: number[];

  public ascDscFilter: number;
  public statusFilter: number;
  public locationFilter: number;
  public stateFilter: number;
  public customerFilter: number;
  public seaiFilter: number;
  public shipmentFilter: number;
  public cartonFilter: number;
  public installerFilter: number;
  public stockOfficeStatusFilter: number;
  private serialIds: number[];
  private lastPrimaryFilter: string;

  static handleRefreshError(err: any): void {
    if (err.status !== 401) {
      console.log('Hub Mail: ' + err);
    }
  }

  constructor(
    private hubService: HubService,
    private route: ActivatedRoute,
  ) { }

  ngOnInit(): void {
    if (!AppComponent.IsLoggedIn()) { return; }
    this.ascDscFilter = 0;
    this.statusFilter = 0;
    this.locationFilter = 0;
    this.stateFilter = 0;
    this.customerFilter = 0;
    this.seaiFilter = seaiAll;
    this.shipmentFilter = 0;
    this.cartonFilter = 0;
    this.installerFilter = 0;
    this.stockOfficeStatusFilter = 0;
    try {
      this.hubService.getLookupTable('customer_types').subscribe(
        lookup_values => this.customer_types = <CustomerType[]>lookup_values,
        error => this.errorMessage = <any>error,
      );
      this.hubService.getLookupTable('stock_statuses').subscribe(
        lookup_values => this.stock_statuses = <StockStatus[]>lookup_values,
        error => this.errorMessage = <any>error,
      );
      this.hubService.getLookupTable('stock_locations').subscribe(
        lookup_values => this.stock_locations = <StockLocation[]>lookup_values,
        error => this.errorMessage = <any>error,
      );
      this.hubService.getLookupTable('installation_states').subscribe(
        lookup_values => this.installation_states = <InstallationState[]>lookup_values,
        error => this.errorMessage = <any>error,
      );
      this.hubService.getLookupTable('installers').subscribe(
        lookup_values => this.installer_list = <Installer[]>lookup_values,
        error => this.errorMessage = <any>error,
      );
      this.hubService.getLookupTable('shipment').subscribe(
        lookup_values => this.shipment_list = <Shipment[]>lookup_values,
        error => this.errorMessage = <any>error,
      );
      this.hubService.getLookupTable('stock_office_status').subscribe(
        lookup_values => this.stock_office_status = <StockOfficeStatus[]>lookup_values,
        error => this.errorMessage = <any>error,
      );
    } catch (e) {
      console.log('Meh: ' + e);
    }

    this.cartons = new Array(100).fill(0);
    for(let i = 0; i < 100; i++){
      this.cartons[i] = i + 1;
    }

    this.refresh('', '', '');
  }

  // might need to remove
  onModMade(modificationsDone: boolean): void {
    if (modificationsDone) {
      this.refresh(this.lastSortOrder, this.lastPrimaryFilter, this.ascDesc[this.ascDscFilter]);
    }
    this.modifyPanelVisible = false;
  }

  filterAscDsc(sortby: string, sortvalue: string): void {
    if (this.ascDscFilter === +sortvalue) {
      this.ascDscFilter = + sortvalue;
      this.hubinfo.reverse();
    }
  }

  filterStatus(sortby: string, sortvalue: string): void {
    this.statusFilter = +sortvalue;
    this.refresh(sortby, sortvalue, this.ascDesc[this.ascDscFilter]);
  }

  filterLocation(sortby: string, sortvalue: string): void {
    this.locationFilter = +sortvalue;
    this.refresh(sortby, sortvalue, this.ascDesc[this.ascDscFilter]);

  }

  filterState(sortby: string, sortvalue: string): void {
    this.stateFilter = +sortvalue;
    this.refresh(sortby, sortvalue, this.ascDesc[this.ascDscFilter]);
  }

  filterCustomer(sortby: string, sortvalue: string): void {
    this.customerFilter = +sortvalue;
    this.refresh(sortby, sortvalue, this.ascDesc[this.ascDscFilter]);
  }

  filterSEAI(sortby: string, sortvalue: string): void {
    this.seaiFilter = +sortvalue;
    this.refresh(sortby, sortvalue, this.ascDesc[this.ascDscFilter]);
  }

  filterShipment(sortby: string, sortvalue: string): void {
    this.shipmentFilter = +sortvalue;
    this.refresh(sortby, sortvalue, this.ascDesc[this.ascDscFilter]);
  }

  filterCarton(sortby: string, sortvalue: string): void {
    this.cartonFilter= +sortvalue;
    this.refresh(sortby, sortvalue, this.ascDesc[this.ascDscFilter]);
  }

  filterInstaller(sortby: string, sortvalue: string): void {
    this.installerFilter = +sortvalue;
    this.refresh(sortby, sortvalue, this.ascDesc[this.ascDscFilter]);
  }

  filterStockOfficeStatus(sortby: string, sortvalue: string): void {
    this.stockOfficeStatusFilter = +sortvalue;
    this.refresh(sortby, sortvalue, this.ascDesc[this.ascDscFilter]);
  }

  modStock(): void {
    this.modifyPanelVisible = true;
  }

  exportStockCSV(): void {
    let tempDate = new Date().toLocaleString();
    const options = {
      filename: `StockReport${tempDate}`,
      useKeysAsHeaders: true,
    };

    const csvExporter = new ExportToCsv(options);

    csvExporter.generateCsv(this.hubinfo);

  }

  // Filter the refreshed list according to all of the other filters currently in effect
  updateStock(stockinfo: HubInformation[]): void {
    this.serialIds = [];
    let stockInfoResult = <HubInformation[]>[];
    for (let info of Object.values(stockinfo)) {
      if (this.statusFilter !== 0 && info.stock_status_id !== this.statusFilter) { continue; }
      if (this.locationFilter !== 0 && info.stock_location_id !== this.locationFilter) { continue; }
      if (this.stateFilter !== 0 && info.installation_state_id !== this.stateFilter) { continue; }
      if (this.customerFilter !== 0 && info.customer_type_id !== this.customerFilter) { continue; }
      //if (this.seaiFilter !== seaiAll && info.seai_trial_group !== this.seaiFilter) { continue; }
      if (this.shipmentFilter !== 0 && info.shipment_number !== this.shipmentFilter) { continue; }
      if (this.cartonFilter !== 0 && info.carton_number !== this.cartonFilter) { continue; }
      if (this.installerFilter !== 0 && info.installer_id !== this.installerFilter) { continue; }
      if (this.stockOfficeStatusFilter !== 0 && info.stock_office_status_id !== this.stockOfficeStatusFilter) { continue; }

      stockInfoResult.push(info);
      this.serialIds.push(info.hubid);
    }
    this.hubinfo = stockInfoResult;
  }

  refresh(sortby: string, filterValue: string, ascdesc: string): void {
    this.lastSortOrder = sortby;
    this.lastPrimaryFilter = filterValue;
    this.route.params.pipe(
      switchMap(() => this.hubService.getStockInformation(sortby, filterValue, ascdesc))
    ).subscribe(
      returnedhubinfo => this.updateStock(returnedhubinfo),
      err => HubStockComponent.handleRefreshError(err)
    );
  }

}

